/* eslint-disable no-console */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import * as signalR from '@microsoft/signalr';

import { Spinner } from '@/components/generic';
import { STREAM_STATUS } from '@/enums';
import { webSocketApiUrl } from '@/helpers';
import { LiveStream } from '@/pages/stream/LiveStream';
import { StreamCoverPhoto } from '@/pages/stream/StreamCoverPhoto';
import { StreamVideo } from '@/pages/stream/StreamVideo';
import { SentoaLogo } from '@/resources';
import { useGetStreamEventByToken, useGetStreamEventStatus } from '@/services/stream.service';

export const WatchStream = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [t] = useTranslation('common');

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${webSocketApiUrl()}/notificationHub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .build();

    connection.on('streamEventUpdated', () => {
      refetch();
    });

    if (connection) {
      connection.start().catch((err) => console.log('err', err));
    }
  }, []);

  const {
    data: streamEventData,
    isLoading: isLoadingStreamEvent,
    refetch
  } = useGetStreamEventByToken(token ?? '');

  const { data: streamStatusData, refetch: refetchStreamStatus } = useGetStreamEventStatus(
    token ?? ''
  );

  useEffect(() => {
    if (streamEventData?.data?.id) {
      const interval = setInterval(() => {
        refetchStreamStatus();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [streamEventData?.data?.id, refetchStreamStatus]);

  useEffect(() => {
    getStreamView();
  }, [streamEventData?.data]);

  useEffect(() => {
    if (streamStatusData?.data) {
      refetch();
    }
  }, [streamStatusData?.data]);

  const getStreamView = () => {
    if (!streamEventData?.data.enabled) {
      return (
        <div className='flex size-full items-center justify-center bg-secondary-steel-blue text-center text-xl text-primary-white'>
          {t('streamNotAvailable')}
        </div>
      );
    }
    if (
      !streamEventData?.data.landingAsset &&
      !streamEventData?.data?.streamUrl &&
      !streamEventData?.data?.streamId
    ) {
      return (
        <div className='flex size-full items-center justify-center bg-secondary-steel-blue text-center text-xl text-primary-white'>
          {streamEventData?.data?.description}
        </div>
      );
    }

    switch (streamEventData?.data.streamStatus) {
      case STREAM_STATUS.NOT_STARTED:
        return (
          <StreamCoverPhoto
            coverPhotoUrl={streamEventData?.data?.landingAsset?.assetUrl}
            waitingForStreamText={streamEventData?.data?.description}
          />
        );
      case STREAM_STATUS.STARTED:
        return <LiveStream streamId={streamEventData?.data?.streamId} />;
      case STREAM_STATUS.ENDED: {
        if (streamEventData?.data.streamId) {
          if (streamEventData?.data?.streamUrl) {
            return <StreamVideo videoUrl={streamEventData?.data?.streamUrl} />;
          } else {
            return <LiveStream streamId={streamEventData?.data?.streamId} />;
          }
        } else {
          return (
            <StreamCoverPhoto
              coverPhotoUrl={streamEventData?.data?.landingAsset?.assetUrl}
              waitingForStreamText={'liveStreamEnded'}
            />
          );
        }
      }
    }
  };

  return (
    <>
      <div className='relative z-10 size-full'>
        {isLoadingStreamEvent && (
          <div className='flex h-screen w-full items-center justify-center'>
            <Spinner />
          </div>
        )}
        {streamEventData?.data && !isLoadingStreamEvent && (
          <div className='flex h-full flex-col items-center'>
            <div className='flex h-14 w-full justify-center bg-color-light-blue px-10 py-2.5 md:justify-start'>
              <SentoaLogo className='mt-1 h-[32px] w-[130px]' />
            </div>
            <div className='mt-4 hidden text-center text-4xl font-bold text-primary-blue md:block'>
              {streamEventData?.data.eventName}
            </div>
            <div className='mt-4 flex aspect-video w-full flex-1 flex-col items-center px-4 md:w-3/4 md:justify-center md:px-0'>
              <div key={`${streamEventData?.data?.streamId}`} className='flex size-full gap-x-10'>
                {getStreamView()}
              </div>
            </div>
          </div>
        )}
      </div>
      <div
        className='fixed left-0 top-0 z-0 h-full w-1/2'
        style={{
          backgroundImage:
            'linear-gradient(to left top, rgba(247, 247, 250, .8), rgba(237, 238, 245, .1), rgb(139, 157, 193, .6))'
        }}
      />
      <div
        className='fixed right-0 top-0 z-0 h-full w-1/2'
        style={{
          backgroundImage:
            'linear-gradient(to right bottom,  rgba(255, 255, 255, .4), rgba(247, 247, 250, .8), rgba(237, 238, 245, .2), rgba(227, 231, 241, 1), rgba(216, 223, 236, 1))'
        }}
      />
    </>
  );
};
